import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import type {
  FilteringOptions,
  PaginatedResponseConsultationSummaryDTO,
  PaginatedResponseConsultationSummaryDTOItemsInner
} from '@/generated'
import useFetch from '@/composables/useFetch'
import { computed } from 'vue'
import usePaginatedTable from '@/composables/usePaginatedTable'
import type { DataTableOptions } from '@/components/common/GenericDataTable.vue'
import useCompletedConsultationFilters, {
  type ConsultationFilters
} from '@/composables/consultation/useCompletedConsultationFilters'

function useCompletedConsultationsTable() {
  const { filters, setNewFilters } = useCompletedConsultationFilters()

  const { execute, error, data, isLoading } = useFetch<
    PaginatedResponseConsultationSummaryDTO,
    [pageIndex: number, itemsPerPage: number, sort: string[], filters: FilteringOptions]
  >((signal, pageIndex, itemsPerPage, sort, filters) =>
    ConsultationService.getCompletedConsultationRequests(
      filters,
      pageIndex,
      itemsPerPage,
      sort,
      signal
    )
  )

  const completedConsultations = computed<PaginatedResponseConsultationSummaryDTOItemsInner[]>(
    () => data.value?.items ?? []
  )

  const totalItems = computed<number>(() => data.value?.totalResults ?? 0)

  const { currentPage, itemsPerPage, sortBy, setCurrentPage, setSortBy } =
    usePaginatedTable(totalItems)

  async function fetchData() {
    const pageIndex = currentPage.value - 1
    const sort = sortBy.value?.length ? [`${sortBy.value[0].key},${sortBy.value[0].order}`] : []
    const filterOptions: FilteringOptions = {
      createdAt: {
        from: filters.value.selectedRange?.[0]?.toISOString() ?? null,
        to: filters.value.selectedRange.at(-1)?.toISOString() ?? null
      },
      patients: filters.value.selectedPatientsIds
    }

    await execute(pageIndex, itemsPerPage.value, sort, filterOptions)
  }

  async function updateOptionsAndFetch(options: DataTableOptions) {
    if (options.page !== currentPage.value) {
      setCurrentPage(options.page)
    }

    const selectedSortValue = options.sortBy[0]

    setSortBy([
      {
        key: selectedSortValue?.key ?? 'createdAt',
        order: selectedSortValue?.order ?? 'desc'
      }
    ])

    await fetchData()
  }

  async function updateFiltersAndFetch(filters: ConsultationFilters) {
    setNewFilters(filters)

    await fetchData()
  }

  return {
    fetchCompletedConsultations: fetchData,
    updateOptionsAndFetch,
    updateFiltersAndFetch,
    isLoading,
    error,
    consultations: completedConsultations,
    consultationFilters: filters,
    totalItems,
    currentPage,
    itemsPerPage,
    sortBy,
    setCurrentPage,
    setSortBy
  }
}

export default useCompletedConsultationsTable
